import React, { useState, useEffect } from 'react';
import { type Bookmark } from '@/types/index';
import { DashboardHeader } from '@/components/DashboardHeader';
import { AddEditForm } from '@/components/AddEditForm';
import { EmptyState } from '@/components/EmptyState';
import { BookmarksGrid } from '@/components/BookmarksGrid';
import { AppLayout, Main } from './AppLayout';

interface DashboardProps {
  bookmarks: Bookmark[];
  onAddBookmark: (bookmark: Bookmark) => void;
  onUpdateBookmark: (bookmark: Bookmark) => void;
  onDeleteBookmark: (id: string) => void;
  onSearchBookmarks: (query: string) => Bookmark[];
  onImport?: () => void;
}

export function Dashboard({
  bookmarks,
  onAddBookmark,
  onUpdateBookmark,
  onDeleteBookmark,
  onSearchBookmarks,
  onImport,
}: DashboardProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingBookmark, setEditingBookmark] = useState<Bookmark | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState('');
  const [image, setImage] = useState<string | null>(null);
  const [favicon, setFavicon] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<'edit' | 'delete' | null>(null);

  const displayedBookmarks = searchQuery.trim() ? onSearchBookmarks(searchQuery) : bookmarks;

  const resetForm = () => {
    setUrl('');
    setTitle('');
    setTags('');
    setImage(null);
    setFavicon(null);
    setShowAddForm(false);
    setEditingBookmark(null);
  };

  const handleAddNew = () => {
    resetForm();
    setShowAddForm(true);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if input or textarea is focused (except for Escape)
      if (
        (e.target as HTMLElement).tagName === 'INPUT' ||
        (e.target as HTMLElement).tagName === 'TEXTAREA'
      ) {
        if (e.key === 'Escape') {
          if (searchQuery) {
            setSearchQuery('');
            e.preventDefault();
            return;
          }
          if (isSearchOpen) {
            setIsSearchOpen(false);
            e.preventDefault();
            return;
          }
        }
        return;
      }

      // Search: / or Cmd+K
      if (e.key === '/' || ((e.metaKey || e.ctrlKey) && e.key === 'k')) {
        e.preventDefault();
        setIsSearchOpen(true);
      }

      // Add: A or Cmd+B
      if (e.key === 'a' || e.key === 'A' || ((e.metaKey || e.ctrlKey) && e.key === 'b')) {
        e.preventDefault();
        handleAddNew();
      }

      // Edit Mode: E
      if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        if (editMode === 'edit') setEditMode(null);
        else if (editMode !== 'delete') setEditMode('edit');
      }

      // Delete Mode: D
      if (e.key === 'd' || e.key === 'D') {
        e.preventDefault();
        if (editMode === 'delete') setEditMode(null);
        else if (editMode !== 'edit') setEditMode('delete');
      }

      // Escape to clear modes
      if (e.key === 'Escape') {
        if (editMode) setEditMode(null);
        if (showAddForm) setShowAddForm(false);
        if (isSearchOpen && !searchQuery) setIsSearchOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editMode, isSearchOpen, searchQuery, showAddForm]);

  const handleSubmit = async (e: React.FormEvent, metadata?: { title: string; image: string | null; favicon: string | null }, normalizedUrl?: string) => {
    e.preventDefault();
    const finalUrl = normalizedUrl || url.trim();
    if (!finalUrl) return;

    const bookmarkData: Bookmark = {
      id: editingBookmark?.id || `bookmark_${Date.now()}`,
      title: title.trim() || metadata?.title || (await getPageTitle(finalUrl)) || new URL(finalUrl).hostname,
      url: finalUrl,
      tags: tags.trim() ? tags.split(',').map(tag => tag.trim()).filter(Boolean) : [],
      image: image || metadata?.image || null,
      favicon: favicon || metadata?.favicon || null,
      createdAt: editingBookmark?.createdAt || new Date().toISOString(),
    };

    if (editingBookmark) {
      onUpdateBookmark(bookmarkData);
    } else {
      onAddBookmark(bookmarkData);
    }

    resetForm();
  };

  const getPageTitle = async (url: string): Promise<string | null> => {
    try {
      return new URL(url).hostname;
    } catch {
      return null;
    }
  };

  const handleEdit = (bookmark: Bookmark) => {
    if (editMode === 'edit') {
      setEditingBookmark(bookmark);
      setUrl(bookmark.url);
      setTitle(bookmark.title);
      setTags(bookmark.tags?.join(', ') || '');
      setImage(bookmark.image || null);
      setFavicon(bookmark.favicon || null);
      setShowAddForm(true);
      setEditMode(null);
    }
  };

  const handleDelete = (id: string) => {
    if (editMode === 'delete') {
      onDeleteBookmark(id);
    }
  };

  return (
    <AppLayout>
      <div className="shrink-0">
        <DashboardHeader
          bookmarksCount={bookmarks.length}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onAddClick={handleAddNew}
          onImportClick={onImport}
          showSearch={true}
          showImport={true}
          editMode={editMode}
          onEditModeChange={setEditMode}
          isSearchOpen={isSearchOpen}
          onSearchOpenChange={setIsSearchOpen}
        />
      </div>

      {/* Add/Edit Form Modal */}
      <AddEditForm
        bookmark={editingBookmark}
        isOpen={showAddForm}
        url={url}
        title={title}
        tags={tags}
        image={image}
        favicon={favicon}
        onUrlChange={setUrl}
        onTitleChange={setTitle}
        onTagsChange={setTags}
        onImageChange={setImage}
        onFaviconChange={setFavicon}
        onSubmit={handleSubmit}
        onClose={resetForm}
      />

      <Main>
        {/* Empty State or Bookmarks Grid */}
        {bookmarks.length === 0 ? (
          <EmptyState onAddClick={handleAddNew} onImportClick={onImport} />
        ) : (
          <BookmarksGrid
            bookmarks={displayedBookmarks}
            searchQuery={searchQuery}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onClearSearch={() => setSearchQuery('')}
            editMode={editMode}
          />
        )}
      </Main>
    </AppLayout>
  );
}
